/**
 * Google Analytics 4 (GA4) Service for Educado AI
 * 
 * Setup Instructions:
 * 1. Go to Google Analytics (https://analytics.google.com)
 * 2. Create a new GA4 property for aiexam.educado.tech
 * 3. Get your Measurement ID (format: G-XXXXXXXXXX)
 * 4. Replace 'G-XXXXXXXXXX' below with your actual Measurement ID
 * 
 * To filter data in GA dashboard:
 * - Both aiexam.educado.tech and educado.tech will have separate properties
 * - Each property will show only its own data
 */

// Google Analytics 4 Measurement ID for aiexam.educado.tech
const GA_MEASUREMENT_ID = 'G-6EZCGY958J';

// Initialize Google Analytics
export const initGA = () => {
  if (typeof window === 'undefined') return;
  
  // Don't initialize if already loaded
  if (window.gtag) return;
  
  // Create gtag script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);
  
  // Initialize gtag
  window.dataLayer = window.dataLayer || [];
  window.gtag = function() {
    window.dataLayer.push(arguments);
  };
  
  window.gtag('js', new Date());
  window.gtag('config', GA_MEASUREMENT_ID, {
    // Enable enhanced measurement
    page_location: window.location.href,
    page_title: document.title,
    // Custom dimension to identify this subdomain
    custom_map: {
      'dimension1': 'site_source'
    }
  });
  
  // Set custom dimension for filtering
  window.gtag('set', { 'site_source': 'aiexam.educado.tech' });
  
  console.log('📊 Google Analytics initialized for aiexam.educado.tech');
};

// Track page views
export const trackPageView = (path, title) => {
  if (!window.gtag) return;
  
  window.gtag('event', 'page_view', {
    page_path: path,
    page_title: title || document.title,
    page_location: window.location.href,
    site_source: 'aiexam.educado.tech'
  });
  
  console.log(`📊 Page view tracked: ${path}`);
};

// Track custom events (button clicks, actions, etc.)
export const trackEvent = (eventName, params = {}) => {
  if (!window.gtag) return;
  
  window.gtag('event', eventName, {
    ...params,
    site_source: 'aiexam.educado.tech'
  });
  
  console.log(`📊 Event tracked: ${eventName}`, params);
};

// Pre-defined event tracking functions for common actions

// Button click tracking
export const trackButtonClick = (buttonName, location = 'unknown') => {
  trackEvent('button_click', {
    button_name: buttonName,
    click_location: location
  });
};

// Assessment events
export const trackAssessmentStart = (isAnonymous = true) => {
  trackEvent('assessment_start', {
    is_anonymous: isAnonymous,
    event_category: 'assessment'
  });
};

export const trackAssessmentComplete = (finalLevel, levelName, totalQuestions) => {
  trackEvent('assessment_complete', {
    final_level: finalLevel,
    level_name: levelName,
    total_questions_answered: totalQuestions,
    event_category: 'assessment'
  });
};

export const trackLevelComplete = (level, levelName, score, totalQuestions) => {
  trackEvent('level_complete', {
    level: level,
    level_name: levelName,
    score: score,
    total_questions: totalQuestions,
    event_category: 'assessment'
  });
};

export const trackLevelFailed = (level, levelName, score) => {
  trackEvent('level_failed', {
    level: level,
    level_name: levelName,
    score: score,
    event_category: 'assessment'
  });
};

export const trackQuestionAnswered = (level, questionIndex, isCorrect, timeSpent) => {
  trackEvent('question_answered', {
    level: level,
    question_index: questionIndex,
    is_correct: isCorrect,
    time_spent_seconds: timeSpent,
    event_category: 'assessment'
  });
};

// User actions
export const trackSignIn = (method = 'google') => {
  trackEvent('sign_in', {
    method: method,
    event_category: 'user'
  });
};

export const trackSignOut = () => {
  trackEvent('sign_out', {
    event_category: 'user'
  });
};

// Share events
export const trackShare = (platform, location = 'unknown') => {
  trackEvent('share', {
    method: platform,
    share_location: location,
    event_category: 'social'
  });
};

// Feedback events
export const trackFeedbackSubmit = (rating, hasComment) => {
  trackEvent('feedback_submit', {
    rating: rating,
    has_comment: hasComment,
    event_category: 'engagement'
  });
};

// Navigation events
export const trackNavigation = (destination, source) => {
  trackEvent('navigation', {
    destination: destination,
    source: source,
    event_category: 'navigation'
  });
};

// External link clicks
export const trackExternalLink = (url, linkName) => {
  trackEvent('external_link_click', {
    link_url: url,
    link_name: linkName,
    event_category: 'outbound'
  });
};

// Results page events
export const trackResultsView = (finalLevel, levelName, percentile) => {
  trackEvent('results_view', {
    final_level: finalLevel,
    level_name: levelName,
    percentile: percentile,
    event_category: 'assessment'
  });
};

export const trackRetakeAssessment = (previousLevel) => {
  trackEvent('retake_assessment', {
    previous_level: previousLevel,
    event_category: 'assessment'
  });
};

// Scroll depth tracking (useful for long pages)
export const trackScrollDepth = (depth, pageName) => {
  trackEvent('scroll_depth', {
    depth_percentage: depth,
    page_name: pageName,
    event_category: 'engagement'
  });
};

export default {
  initGA,
  trackPageView,
  trackEvent,
  trackButtonClick,
  trackAssessmentStart,
  trackAssessmentComplete,
  trackLevelComplete,
  trackLevelFailed,
  trackQuestionAnswered,
  trackSignIn,
  trackSignOut,
  trackShare,
  trackFeedbackSubmit,
  trackNavigation,
  trackExternalLink,
  trackResultsView,
  trackRetakeAssessment,
  trackScrollDepth
};
